/* HDR → SDR — the one door an HDR video frame (HLG / PQ, iPhone since iOS 13) takes into an SDR picture: the export
   rigs of webcodecs-pipeline.js, its 2D lane and VideoOrientation.draw (frame grabs, previews). The FFmpeg lanes
   reach the same target with CodecMuxRules.hdrToSdrFilter(). Why and the measurements — docs/hdr-to-sdr.md.

   What the browser hands us (measured 2026-10-22, Chrome 156 / Windows, real decoder frames and <video>):
     · texImage2D of an HDR frame = the coded signal itself, 8-bit, BT.2020 primaries — no transfer, no gamut
       conversion. A frame built on the CPU goes a THIRD way (clipped at HLG 0.8), and another build of Chromium
       converts it like 2D. The upload cannot be trusted to mean one thing.
     · 2D drawImage = the browser's own colour-managed picture — the same one the <video> shows on screen, the same
       for CPU and GPU frames — but tone-mapped dim: HLG reference white lands at ~70 % of SDR white.
   So the door draws the frame through 2D (the one path that is consistent) and corrects it per channel with
   g = T ∘ f⁻¹: f is the browser's own 2D curve, measured HERE on a known ramp frame, T is the target below. Against
   the exact conversion of the coded signal: 40–47 dB on six clips × HLG/PQ.

   The target T (VMAF NEG against the SDR original, 6 clips × HLG/PQ × {SDR range, 1000-nit highlights}):
   BT.2100 display light (HLG: Lw 1000 nits, system gamma 1.2; PQ: absolute), BT.2408 reference white 203 nits =
   SDR white, Möbius roll-off on max(R,G,B) from 0.9 of white to the 1000-nit peak, BT.1886 (2.4) encode. The rig's
   former Hable pass ran on the 8-bit coded signal as if it were linear light: −51 levels of luma, NEG 0. */
(function () {
'use strict';
if (typeof window === 'undefined' || window.HdrToSdr) return;

var REF = 203, LW = 1000, J = 0.9, PEAK = LW / REF;
var HA = 0.17883277, HB = 0.28466892, HC = 0.55991073;
var M1 = 2610 / 16384, M2 = 2523 / 4096 * 128, C1 = 3424 / 4096, C2 = 2413 / 4096 * 32, C3 = 2392 / 4096 * 32;
var MA = -J * J * (PEAK - 1) / (J * J - 2 * J + PEAK), MB = (J * J - 2 * J * PEAK + PEAK) / (PEAK - 1);

function hlgNits(s) {                  // gray: inverse OETF, then the OOTF (Ys = E for gray)
    var e = s <= 0 ? 0 : (s <= 0.5 ? s * s / 3 : (Math.exp((s - HC) / HA) + HB) / 12);
    return LW * Math.pow(e, 1.2);
}
function pqNits(s) {
    var p = Math.pow(Math.max(0, Math.min(1, s)), 1 / M2);
    return 10000 * Math.pow(Math.max(p - C1, 0) / (C2 - C3 * p), 1 / M1);
}
function mobius(x) {                   // x in units of reference white; slope 1 at J, exactly 1 at the peak
    if (x <= J) return x;
    return Math.min(1, (MB * MB + 2 * MB * J + J * J) / (MB - MA) * (x + MA) / (x + MB));
}
/* T on a gray signal s (0..1 of the coded range): the SDR value, 0..1 */
function target(s, tf) {
    var nits = tf === 'pq' ? pqNits(s) : hlgNits(s);
    return Math.pow(mobius(nits / REF), 1 / 2.4);
}

/* The transfer a source needs converting from: 'hlg' | 'pq' | 'b2020' (SDR on BT.2020 primaries — only the
   gamut, which 2D does and a texture upload does not) | null. A <video> answers through a VideoFrame of its
   current picture; the answer is kept per SOURCE URL (every element showing the file shares it). Not kept: a
   failed read (no picture to read yet, or a parked element — "Invalid source state") and an untagged picture
   (transfer null), which is asked again — up to 30 times, then taken as SDR. Measured 22.10.2026 on /enhance-video:
   the paused preview of a PQ file was captured at readyState 1 right after a presented seek, and the old
   «readyState ≥ 2 or nothing» rule sent that frame through as the browser's dim picture (87 against 128). */
var _srcTf = {}, _srcAsks = {};
function transferOfCs(cs) {
    if (!cs) return null;
    var t = cs.transfer;
    if (t === 'hlg' || t === 'arib-std-b67') return 'hlg';
    if (t === 'pq' || t === 'smpte2084') return 'pq';
    if (cs.primaries === 'bt2020') return 'b2020';
    return null;
}
function transferOf(source) {
    if (!source) return null;
    try {
        if (typeof VideoFrame !== 'undefined' && source instanceof VideoFrame) return transferOfCs(source.colorSpace);
        if (typeof HTMLVideoElement !== 'undefined' && source instanceof HTMLVideoElement) {
            if (typeof VideoFrame === 'undefined') return null;
            var src = source.currentSrc || source.src || '';
            if (src && Object.prototype.hasOwnProperty.call(_srcTf, src)) return _srcTf[src];
            var vf;
            try { vf = new VideoFrame(source); } catch (_) { return null; }
            var cs = vf.colorSpace;
            var tf = transferOfCs(cs);
            var tagged = !!(cs && (cs.transfer || cs.primaries));
            vf.close();
            if (src && (tf || tagged || (_srcAsks[src] = (_srcAsks[src] || 0) + 1) >= 30)) _srcTf[src] = tf;
            return tf;
        }
    } catch (_) {}
    return null;
}

/* f — the browser's 2D picture of a gray ramp over every 10-bit code (64..940), measured once per transfer.
   The LUT g[c] = 255·T(f⁻¹(c)): the mean signal of every code the browser drew as c. Codes the browser never
   produces are interpolated; the table is made non-decreasing. null → the door stays shut for that transfer
   (the browser's own picture goes through, as before the door). */
var _curves = {};
function curveFor(tf) {
    if (Object.prototype.hasOwnProperty.call(_curves, tf)) return _curves[tf];
    var t0 = Date.now(), res = null, why = '';
    try {
        var N = 877, W = 878, H = 2;
        var y = new Uint16Array(W * H), uv = new Uint16Array(W * H / 4);
        for (var r = 0; r < H; r++) for (var i = 0; i < W; i++) y[r * W + i] = 64 + Math.min(i, N - 1);
        uv.fill(512);
        var buf = new Uint8Array((y.length + 2 * uv.length) * 2);
        buf.set(new Uint8Array(y.buffer), 0);
        buf.set(new Uint8Array(uv.buffer), y.length * 2);
        buf.set(new Uint8Array(uv.buffer), (y.length + uv.length) * 2);
        var vf = new VideoFrame(buf, { format: 'I420P10', codedWidth: W, codedHeight: H, timestamp: 0,
            colorSpace: { primaries: 'bt2020', transfer: tf, matrix: 'bt2020-ncl', fullRange: false } });
        var cv = new OffscreenCanvas(W, H), cx = cv.getContext('2d', { willReadFrequently: true });
        cx.drawImage(vf, 0, 0);
        vf.close();
        var d = cx.getImageData(0, 0, W, 1).data, f = new Uint8Array(N);
        for (var k = 0; k < N; k++) f[k] = d[k * 4 + 1];
        if (f[N - 1] - f[0] < 128) { why = 'flat'; throw new Error('flat'); }
        res = { f: f, lut: buildLut(f, tf) };
    } catch (e) { if (!why) why = String((e && e.name) || e).slice(0, 40); }
    _curves[tf] = res;
    try {
        if (typeof window.__ae === 'function') {
            var m = document.querySelector('meta[name="tool-slug"]');
            var p = { slug: (m && m.content) || '', action: 'hdr_sdr_door', tf: tf, ok: res ? 1 : 0, ms: Date.now() - t0 };
            if (res) { p.f25 = res.f[219]; p.f50 = res.f[438]; p.f75 = res.f[657]; p.f100 = res.f[876]; p.g128 = res.lut[128]; }
            else p.why = why;
            window.__ae('feature_use', p);
        }
    } catch (_) {}
    return res;
}
function buildLut(f, tf) {
    var N = f.length, sum = new Float64Array(256), cnt = new Uint32Array(256), g = new Float64Array(256), k, c;
    for (k = 0; k < N; k++) { sum[f[k]] += target(k / (N - 1), tf); cnt[f[k]]++; }
    var known = [];
    for (c = 0; c < 256; c++) if (cnt[c]) { g[c] = sum[c] / cnt[c]; known.push(c); }
    // black stays black: the browser's 0 also holds every deep shadow it crushed, and their mean (2–5 levels)
    // would turn true black grey
    g[0] = 0;
    if (!cnt[0]) known.unshift(0);
    if (!cnt[255]) { g[255] = 1; known.push(255); }
    for (k = 0; k + 1 < known.length; k++) {
        var a = known[k], b = known[k + 1];
        for (c = a + 1; c < b; c++) g[c] = g[a] + (g[b] - g[a]) * (c - a) / (b - a);
    }
    var out = new Uint8Array(256), hi = 0;
    for (c = 0; c < 256; c++) { hi = Math.max(hi, Math.round(g[c] * 255)); out[c] = hi; }
    return out;
}

/* The GL pass: a canvas through the 256-entry table, per channel. One context per page; lost → rebuilt once. */
var _gl = null;
var VS = '#version 300 es\nin vec2 p; out vec2 uv; void main(){ uv = p * 0.5 + 0.5; gl_Position = vec4(p, 0.0, 1.0); }';
var FS = '#version 300 es\nprecision highp float; in vec2 uv; out vec4 o; uniform sampler2D img; uniform sampler2D lut;\n' +
    'float m(float v){ return texture(lut, vec2((floor(v * 255.0 + 0.5) + 0.5) / 256.0, 0.5)).r; }\n' +
    'void main(){ vec3 c = texture(img, vec2(uv.x, 1.0 - uv.y)).rgb; o = vec4(m(c.r), m(c.g), m(c.b), 1.0); }';
function glPass() {
    if (_gl && !_gl.gl.isContextLost()) return _gl;
    _gl = null;
    try {
        var cv = new OffscreenCanvas(16, 16);
        var gl = cv.getContext('webgl2', { premultipliedAlpha: false, preserveDrawingBuffer: true, antialias: false });
        if (!gl) return null;
        var sh = function (type, src) { var s = gl.createShader(type); gl.shaderSource(s, src); gl.compileShader(s); return s; };
        var pr = gl.createProgram();
        gl.attachShader(pr, sh(gl.VERTEX_SHADER, VS)); gl.attachShader(pr, sh(gl.FRAGMENT_SHADER, FS));
        gl.bindAttribLocation(pr, 0, 'p'); gl.linkProgram(pr);
        if (!gl.getProgramParameter(pr, gl.LINK_STATUS)) return null;
        var vb = gl.createBuffer(); gl.bindBuffer(gl.ARRAY_BUFFER, vb);
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]), gl.STATIC_DRAW);
        var vao = gl.createVertexArray(); gl.bindVertexArray(vao);
        gl.enableVertexAttribArray(0); gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
        var tex = function () {
            var t = gl.createTexture(); gl.bindTexture(gl.TEXTURE_2D, t);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
            return t;
        };
        gl.useProgram(pr);
        gl.uniform1i(gl.getUniformLocation(pr, 'img'), 0);
        gl.uniform1i(gl.getUniformLocation(pr, 'lut'), 1);
        _gl = { cv: cv, gl: gl, img: tex(), lut: tex(), lutOf: null };
        return _gl;
    } catch (_) { return null; }
}
function throughLut(stage, lut) {
    var g = glPass();
    if (!g) return null;
    var gl = g.gl, w = stage.width, h = stage.height;
    try {
        if (g.cv.width !== w) g.cv.width = w;
        if (g.cv.height !== h) g.cv.height = h;
        gl.viewport(0, 0, w, h);
        gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
        if (g.lutOf !== lut) {
            var px = new Uint8Array(256 * 4);
            for (var c = 0; c < 256; c++) { px[c * 4] = px[c * 4 + 1] = px[c * 4 + 2] = lut[c]; px[c * 4 + 3] = 255; }
            gl.activeTexture(gl.TEXTURE1); gl.bindTexture(gl.TEXTURE_2D, g.lut);
            gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 256, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, px);
            g.lutOf = lut;
        }
        // the upload stays unflipped (row 0 = the picture's top); the shader reads uv.y upside down, because
        // the quad's uv.y = 0 is the bottom of the canvas
        gl.activeTexture(gl.TEXTURE0); gl.bindTexture(gl.TEXTURE_2D, g.img);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, stage);
        gl.drawArrays(gl.TRIANGLES, 0, 6);
        return g.cv;
    } catch (_) { return null; }
}

var _stage = null;
function sizeOf(source) {
    if (typeof VideoFrame !== 'undefined' && source instanceof VideoFrame) return { w: source.displayWidth, h: source.displayHeight };
    return { w: source.videoWidth || source.width || 0, h: source.videoHeight || source.height || 0 };
}
/* The SDR picture of `source` (VideoFrame | <video>), the same size and orientation drawImage(source) gives:
   `source` itself when it is SDR — or a canvas the caller draws/uploads BEFORE the next call (both canvases are
   reused). `opts.texture`: the caller uploads with texImage2D, which converts nothing — an SDR picture on BT.2020
   primaries needs the 2D stage too. */
function image(source, opts) {
    var tf = transferOf(source);
    if (!tf) return source;
    var forTexture = !!(opts && opts.texture);
    var cur = tf === 'b2020' ? null : curveFor(tf);
    if (!forTexture && !cur) return source;
    var sz = sizeOf(source);
    if (!(sz.w > 0 && sz.h > 0)) return source;
    try {
        if (!_stage) { _stage = new OffscreenCanvas(sz.w, sz.h); _stage.__ctx = _stage.getContext('2d'); }
        if (_stage.width !== sz.w) _stage.width = sz.w;
        if (_stage.height !== sz.h) _stage.height = sz.h;
        _stage.__ctx.drawImage(source, 0, 0, sz.w, sz.h);
    } catch (_) { return source; }
    if (!cur) return _stage;
    return throughLut(_stage, cur.lut) || (forTexture ? _stage : source);
}

window.HdrToSdr = {
    REF: REF, PEAK_NITS: LW, KNEE: J,
    target: target,
    transferOf: transferOf,
    curveFor: curveFor,
    buildLut: buildLut,
    image: image
};
})();
